import { useMemo, useRef } from 'react';
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch';

/**
 * Wraps useInfiniteHits with a self-managed accumulation fallback for
 * React 19 + Next.js App Router environments where the connector's internal
 * cache can fail to populate due to deferred setState timing.
 *
 * Returns the connector's data when available; otherwise accumulates hits
 * page-by-page from results.hits.
 */
const useHitsAccumulation = () => {
  const {
    items: connectorItems,
    showMore,
    isLastPage: connectorIsLastPage
  } = useInfiniteHits({
    skipSuspense: true
  });
  const {
    results
  } = useInstantSearch({
    skipSuspense: true
  });
  const accRef = useRef({
    hits: {},
    key: ''
  });
  const items = useMemo(() => {
    if (connectorItems?.length > 0) return connectorItems;
    // eslint-disable-next-line no-underscore-dangle
    if (!results?.hits?.length || results.__isArtificial) return connectorItems;

    // Reset accumulation when query/filters/index change
    const paramsWithoutPage = (results.params || '').split('&').filter(p => !p.startsWith('page=')).join('&');
    const key = `${results.index ?? ''}-${results.query ?? ''}-${paramsWithoutPage}`;
    if (key !== accRef.current.key) {
      accRef.current = {
        hits: {},
        key
      };
    }
    accRef.current.hits[results.page ?? 0] = results.hits;
    return Object.keys(accRef.current.hits).map(Number).sort((a, b) => a - b).flatMap(p_0 => accRef.current.hits[p_0]);
  }, [connectorItems, results]);
  const isLastPage = connectorItems?.length > 0 ? connectorIsLastPage : results?.nbPages != null && results?.page != null ? results.nbPages <= results.page + 1 : connectorIsLastPage;
  return {
    items,
    isLastPage,
    showMore
  };
};
export default useHitsAccumulation;